import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Booking } from '../../../../core/models/entities/booking';
import {
  Invoice,
  InvoiceCategoryName,
  InvoiceLine,
  InvoicingStatus,
  InvoicingStatusHumanized,
  PayrexxInvoiceStatus,
} from '../../../../core/models/entities/invoice';
import { ChfPipe } from '../../../../core/pipes/chf.pipe';

@Component({
  selector: 'app-customer-invoices-table',
  providers: [ChfPipe],
  template: `
    <div>
      <!-- Header -->
      <div class="mb-4 flex items-start justify-between">
        <h3>{{ 'customer-invoices.title' | transloco }}</h3>
        @if (booking().invoicing_status && booking().balance !== 0) {
          <app-badge
            [variant]="InvoicingStatusHumanized[booking().invoicing_status].color"
            class="ml-4"
          >
            {{ balanceLabel() }} : {{ booking().balance | chf }}
          </app-badge>
        }
        @if (booking().balance === 0) {
          <app-badge variant="success" class="ml-4">{{
            'common.invoicing-status.' + booking().invoicing_status | transloco
          }}</app-badge>
        }
      </div>

      <h4 class="mb-4 text-base font-medium">{{ 'customer-invoices.invoices-subtitle' | transloco }}</h4>

      <!-- Table : un seul grid partagé pour aligner header et lignes -->
      <div class="grid grid-cols-[1fr_120px_auto] overflow-hidden rounded-lg border border-gray-200">

        <!-- En-têtes -->
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ 'customer-invoices.col-document' | transloco }}</div>
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ 'customer-invoices.col-date' | transloco }}</div>
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">{{ 'customer-invoices.col-amount' | transloco }}</div>

        @for (invoice of invoices(); track invoice.id; let last = $last) {

          <!-- Col 1 : Document -->
          <div
            class="px-4 py-3"
            [class.border-b]="!last || isExpanded(invoice.id)"
            [class.border-gray-200]="!last || isExpanded(invoice.id)"
          >
            <div class="flex items-center gap-2">
              @if (invoice.metadata?.payrexx_id) {
                <span class="font-medium">
                  {{ (invoice.series ? 'customer-invoices.invoice-prefix-series' : 'customer-invoices.invoice-prefix') | transloco: { id: invoice.booking_id, series: invoice.series } }}
                </span>
                <i class="far fa-question-circle text-gray-400 text-xs" [tooltip]="'customer-invoices.payrexx-tooltip' | transloco"></i>
              } @else {
                <span class="font-medium">
                  {{ (invoice.series ? 'customer-invoices.document-prefix-series' : 'customer-invoices.document-prefix') | transloco: { id: invoice.booking_id, series: invoice.series } }}
                </span>
                <i class="far fa-question-circle text-gray-400 text-xs" [tooltip]="'customer-invoices.document-tooltip' | transloco"></i>
              }
            </div>
            @if (invoice.grouped_invoice_lines) {
              <button
                type="button"
                class="mt-1 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                (click)="toggleInvoice(invoice.id)"
              >
                @if (isExpanded(invoice.id)) {
                  {{ 'customer-invoices.hide-detail' | transloco }} <i class="far fa-chevron-up text-xs"></i>
                } @else {
                  {{ 'customer-invoices.see-detail' | transloco }} <i class="far fa-chevron-down text-xs"></i>
                }
              </button>
            }
          </div>

          <!-- Col 2 : Date -->
          <div
            class="px-4 py-3 text-sm text-gray-500"
            [class.border-b]="!last || isExpanded(invoice.id)"
            [class.border-gray-200]="!last || isExpanded(invoice.id)"
          >
            {{ invoice.created_at | date: 'dd.MM.yyyy' }}
          </div>

          <!-- Col 3 : Montant + Actions -->
          <div
            class="px-4 py-3 text-right text-sm"
            [class.border-b]="!last || isExpanded(invoice.id)"
            [class.border-gray-200]="!last || isExpanded(invoice.id)"
          >
            <span class="font-semibold">{{ invoice.net_total | chf }}</span>
            @if (invoice.metadata?.payrexx_status === PayrexxInvoiceStatus.Deleted) {
              <div class="text-xs text-red-500">{{ 'customer-invoices.deleted' | transloco }}</div>
            } @else if (invoice.metadata?.payrexx_status === PayrexxInvoiceStatus.Draft) {
              <div class="text-xs italic text-gray-500">{{ 'customer-invoices.pending' | transloco }}</div>
            }
            @if (!invoice.metadata?.payrexx_status || (invoice.metadata?.payrexx_status !== PayrexxInvoiceStatus.Deleted && invoice.metadata?.payrexx_status !== PayrexxInvoiceStatus.Draft)) {
              @if (invoice.download_url || invoice.metadata?.payrexx_payment_link) {
                <div class="mt-2 flex justify-end gap-2">
                  @if (invoice.download_url) {
                    <a
                      [href]="invoice.download_url"
                      target="_blank"
                      class="inline-flex items-center justify-center gap-x-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-xs no-underline transition-all hover:bg-gray-50 hover:border-gray-300 focus:outline-none"
                    >
                      <span class="hidden sm:inline">{{ 'customer-invoices.download' | transloco }}</span>
                      <span class="sm:hidden">{{ 'customer-invoices.download-short' | transloco }}</span>
                    </a>
                  }
                  @if (invoice.metadata?.payrexx_payment_link) {
                    <a
                      [href]="invoice.metadata.payrexx_payment_link"
                      target="_blank"
                      class="inline-flex items-center justify-center gap-x-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-xs no-underline transition-all hover:bg-gray-50 hover:border-gray-300 focus:outline-none"
                    >
                      <span class="hidden sm:inline">{{ 'customer-invoices.pay-online' | transloco }}</span>
                      <span class="sm:hidden">{{ 'customer-invoices.pay-online-short' | transloco }}</span>
                    </a>
                  }
                </div>
              }
            }
          </div>

          <!-- Détail déplié (toute la largeur) -->
          @if (isExpanded(invoice.id) && invoice.grouped_invoice_lines) {
            <div
              class="col-span-full bg-gray-50 px-4 pb-4 pt-3"
              [class.border-b]="!last"
              [class.border-gray-200]="!last"
            >
              @for (category of getInvoiceCategories(invoice); track category) {
                @if (getInvoiceCategories(invoice).length > 1 && category) {
                  <div class="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {{ category }}
                  </div>
                }
                @for (line of invoice.grouped_invoice_lines[category]; track line.id) {
                  <div class="py-1.5">
                    <div class="min-w-0 break-words text-sm text-gray-700" [innerHTML]="lineDescription(invoice, category, line)"></div>
                    @if (line.unit_price) {
                      <div class="mt-0.5 text-xs text-gray-400">
                        {{ formatLinePriceDetail(line) }}
                      </div>
                    }
                  </div>
                }
              }
              <div class="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
                <span class="text-sm font-semibold">{{ 'customer-invoices.total' | transloco }}</span>
                <span class="text-sm font-semibold">{{ invoice.net_total | chf }}</span>
              </div>
            </div>
          }
        }
      </div>

      <!-- Paiements Section -->
      @if (booking().payments && booking().payments.length > 0) {
        <div class="mt-8">
          <h4 class="mb-4 text-base font-medium">{{ 'customer-invoices.payments-subtitle' | transloco }}</h4>
          <app-customer-payments-table
            [booking]="booking()"
            [payments]="booking().payments"
          >
          </app-customer-payments-table>
        </div>
      }
    </div>
  `,
  standalone: false,
})
export class CustomerInvoicesTableComponent {
  booking = input.required<Booking>();
  invoices = input<Invoice[]>([]);

  private chfPipe = inject(ChfPipe);
  private translocoService = inject(TranslocoService);
  private expandedInvoices = signal<Set<number>>(new Set());

  InvoicingStatus = InvoicingStatus;
  PayrexxInvoiceStatus = PayrexxInvoiceStatus;
  InvoicingStatusHumanized = InvoicingStatusHumanized;

  balanceLabel = computed(() => {
    return this.booking().balance < 0
      ? this.translocoService.translate('customer-invoices.balance-refund')
      : this.translocoService.translate('customer-invoices.balance-due');
  });

  toggleInvoice(invoiceId: number) {
    const next = new Set(this.expandedInvoices());
    next.has(invoiceId) ? next.delete(invoiceId) : next.add(invoiceId);
    this.expandedInvoices.set(next);
  }

  isExpanded(invoiceId: number): boolean {
    return this.expandedInvoices().has(invoiceId);
  }

  getInvoiceCategories(invoice: Invoice): string[] {
    return Object.keys(invoice.grouped_invoice_lines || {});
  }

  /**
   * La ligne d'un bon de réduction est enregistrée avec une description figée : on lui
   * substitue le nom du bon. Le code ne sert de repli que si le bon n'a pas de nom.
   */
  lineDescription(invoice: Invoice, category: string, line: InvoiceLine): string {
    if (category !== InvoiceCategoryName.Coupon || !invoice.coupon) {
      return line.description;
    }

    return (
      invoice.coupon.name?.trim() || (invoice.coupon.code ?? '').toUpperCase()
    );
  }

  formatLinePriceDetail(line: InvoiceLine): string {
    const qty = `${line.quantity} ${line.units || ''}`.trim().replace(/\s*heures?\b/g, 'h');
    const unitPrice = this.chfPipe.transform(line.unit_price);
    const total = this.chfPipe.transform(line.total_price);
    return `${qty} x ${unitPrice} = ${total}`;
  }
}
